import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Menu, X, ShoppingCart, ChevronLeft, Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";
const WEBHOOK_SECRET = "blkmrkt_email_webhook_2024";

export default function AdminEmailProcessor() {
  const { user, logout, getAuthHeader } = useAuth();
  const { itemCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const [emailContent, setEmailContent] = useState('');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [emailType, setEmailType] = useState('auto'); // 'auto', 'paypal', 'pirateship'

  const processEmail = async () => {
    if (!emailContent.trim()) {
      toast.error('Please paste email content');
      return;
    }
    
    setProcessing(true);
    setResult(null);
    
    try {
      const response = await axios.post(
        `${API}/api/webhooks/parse-email`,
        {
          content: emailContent,
          from: emailType === 'paypal' ? 'service@paypal.com' : 
                emailType === 'pirateship' ? 'noreply@pirateship.com' : '',
          subject: ''
        },
        {
          headers: {
            ...getAuthHeader(),
            'X-Webhook-Secret': WEBHOOK_SECRET
          }
        }
      );
      
      setResult(response.data);
      
      if (response.data.success) {
        toast.success(`Order ${response.data.order_id} updated to ${response.data.new_status}`);
        setEmailContent('');
      } else {
        toast.error(response.data.error || 'Failed to process email');
      }
    } catch (error) {
      console.error('Error processing email:', error);
      toast.error(error.response?.data?.detail || 'Failed to process email');
      setResult({ success: false, error: error.message });
    } finally {
      setProcessing(false);
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white font-mono">Access denied</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-zinc-900 sticky top-0 bg-black z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/admin" className="text-zinc-400 hover:text-white font-mono text-sm">
                DASHBOARD
              </Link>
              <Link to="/admin/analytics" className="text-zinc-400 hover:text-white font-mono text-sm">
                ANALYTICS
              </Link>
              <Link to="/cart" className="relative">
                <ShoppingCart className="w-5 h-5 text-zinc-400 hover:text-white" />
                {itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-tiffany-500 text-black text-xs w-5 h-5 flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Link>
              <button onClick={logout} className="text-zinc-500 hover:text-white font-mono text-sm">
                LOGOUT
              </button>
            </div>

            <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-zinc-950 border-b border-zinc-900 px-4 py-4 space-y-4">
          <Link to="/admin" className="block text-zinc-400 font-mono text-sm">DASHBOARD</Link>
          <Link to="/admin/analytics" className="block text-zinc-400 font-mono text-sm">ANALYTICS</Link>
          <button onClick={logout} className="text-zinc-500 font-mono text-sm">LOGOUT</button>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <a 
          href="/admin"
          className="flex items-center gap-2 text-zinc-500 hover:text-white mb-6 font-mono text-sm"
        >
          <ChevronLeft className="w-4 h-4" />
          BACK TO DASHBOARD
        </a>

        <div className="flex items-center gap-3 mb-8">
          <Mail className="w-6 h-6 text-tiffany-500" />
          <h1 className="text-2xl font-sans text-white">EMAIL PROCESSOR</h1>
        </div>

        <div className="space-y-6">
          {/* Instructions */}
          <div className="bg-zinc-900/50 border border-zinc-800 p-4">
            <h3 className="font-mono text-sm text-tiffany-500 mb-2">HOW TO USE</h3>
            <ol className="font-mono text-xs text-zinc-400 space-y-1 list-decimal list-inside">
              <li>Open the PayPal or PirateShip notification email</li>
              <li>Select all and copy the email content (text only)</li>
              <li>Paste below and click Process</li>
              <li>The order will be automatically updated</li>
            </ol>
          </div>

          {/* Email Type Selection */}
          <div>
            <label className="font-mono text-xs text-zinc-500 block mb-2">EMAIL TYPE</label>
            <div className="flex gap-3">
              <button
                onClick={() => setEmailType('auto')}
                className={`px-4 py-2 font-mono text-xs border ${
                  emailType === 'auto' 
                    ? 'border-tiffany-500 text-tiffany-500 bg-tiffany-500/10' 
                    : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                }`}
              >
                AUTO-DETECT
              </button>
              <button
                onClick={() => setEmailType('paypal')}
                className={`px-4 py-2 font-mono text-xs border ${
                  emailType === 'paypal' 
                    ? 'border-blue-500 text-blue-500 bg-blue-500/10' 
                    : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                }`}
              >
                PAYPAL
              </button>
              <button
                onClick={() => setEmailType('pirateship')}
                className={`px-4 py-2 font-mono text-xs border ${
                  emailType === 'pirateship' 
                    ? 'border-purple-500 text-purple-500 bg-purple-500/10' 
                    : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                }`}
              >
                PIRATESHIP
              </button>
            </div>
          </div>

          {/* Email Content Input */}
          <div>
            <label className="font-mono text-xs text-zinc-500 block mb-2">
              PASTE EMAIL CONTENT
            </label>
            <Textarea
              value={emailContent}
              onChange={(e) => setEmailContent(e.target.value)}
              placeholder="Paste the full email content here...

Example PayPal:
anthony hurtado sent you $187.00 USD
Amount: $187.00 USD
Note from anthony hurtado: BLK-059374
Transaction ID: 8XP07924GF791304P

Example PirateShip:
Dear Anthony,
Your shipment is on its way!
Shipped To: 4510 E Banner Gateway Dr...
Track Your Shipment: 9400136106195374499234"
              className="bg-zinc-900 border-zinc-700 text-white font-mono text-sm min-h-[300px] rounded-none"
            />
          </div>

          {/* Process Button */}
          <Button
            onClick={processEmail}
            disabled={processing || !emailContent.trim()}
            className="w-full bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none font-mono py-6"
          >
            {processing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                PROCESSING...
              </>
            ) : (
              'PROCESS EMAIL'
            )}
          </Button>

          {/* Result Display */}
          {result && (
            <div className={`border p-4 ${
              result.success 
                ? 'border-green-500 bg-green-500/10' 
                : 'border-red-500 bg-red-500/10'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {result.success ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-500" />
                )}
                <span className={`font-mono text-sm ${result.success ? 'text-green-500' : 'text-red-500'}`}>
                  {result.success ? 'SUCCESS' : 'FAILED'}
                </span>
              </div>
              
              <div className="font-mono text-xs space-y-1">
                {result.order_id && (
                  <p className="text-white">Order: <span className="text-tiffany-500">{result.order_id}</span></p>
                )}
                {result.new_status && (
                  <p className="text-white">New Status: <span className="text-green-500">{result.new_status}</span></p>
                )}
                {result.tracking_number && (
                  <p className="text-white">Tracking: <span className="text-tiffany-500">{result.tracking_number}</span></p>
                )}
                {result.transaction_id && (
                  <p className="text-white">PayPal TX: <span className="text-tiffany-500">{result.transaction_id}</span></p>
                )}
                {result.error && (
                  <p className="text-red-400">Error: {result.error}</p>
                )}
                {result.order_number && !result.success && (
                  <p className="text-zinc-400">Searched for: {result.order_number}</p>
                )}
              </div>
            </div>
          )}

          {/* Help Section */}
          <div className="border border-zinc-800 p-4 mt-8">
            <h3 className="font-mono text-sm text-zinc-400 mb-3">ORDER STATUS FLOW</h3>
            <div className="space-y-2 font-mono text-xs">
              <div className="flex items-center gap-2">
                <span className="text-yellow-500">1. PENDING PAYMENT</span>
                <span className="text-zinc-600">→</span>
                <span className="text-zinc-400">Order placed, awaiting payment</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-purple-500">2. PENDING FULFILLMENT</span>
                <span className="text-zinc-600">→</span>
                <span className="text-zinc-400">PayPal payment confirmed (auto via email)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-tiffany-500">3. PENDING SHIPMENT</span>
                <span className="text-zinc-600">→</span>
                <span className="text-zinc-400">Label printed, tracking added (auto via email)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-blue-500">4. SHIPPED</span>
                <span className="text-zinc-600">→</span>
                <span className="text-zinc-400">Manually set by admin (final status)</span>
              </div>
            </div>
          </div>

          <div className="border border-zinc-800 p-4">
            <h3 className="font-mono text-sm text-zinc-400 mb-3">WHAT THIS DOES</h3>
            <div className="space-y-3 font-mono text-xs text-zinc-500">
              <div className="flex items-start gap-2">
                <span className="text-blue-500">PAYPAL:</span>
                <span>Extracts order number, amount, and transaction ID. Verifies amount matches order. Updates status to "Pending Fulfillment".</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-purple-500">PIRATESHIP:</span>
                <span>Extracts tracking number and shipping address. Matches to order by address. Updates status to "Pending Shipment" with tracking.</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
